"use client"

import { useCallback, useEffect, useRef, useState } from "react"

import { canShareNatively, canShareViaLegacyHost, shareNatively, shareViaLegacyHost } from "@/platform/bridge/native-share"
import type { ShareChannel, ShareStoryData } from "@/platform/bridge/types"
import { useNativeBridge } from "@/platform/bridge/use-native-bridge"

export type ShareFeedback = "code" | "link" | "story" | "failed" | null

const STORY_FILE_NAME = "nelo-codigo.png"

const FEEDBACK_MS = 2500

const DOWNLOAD_URL_LIFETIME_MS = 60_000

const isCancelled = (error: unknown) => error instanceof DOMException && error.name === "AbortError"

const isShareInProgress = (error: unknown) => error instanceof DOMException && error.name === "InvalidStateError"

const copyWithSelection = (value: string) => {
  const field = document.createElement("textarea")
  field.value = value
  field.setAttribute("readonly", "")
  field.style.position = "fixed"
  field.style.opacity = "0"
  document.body.appendChild(field)
  try {
    field.select()
    return document.execCommand("copy")
  } catch {
    return false
  } finally {
    field.remove()
  }
}

const downloadFile = (file: File) => {
  const url = URL.createObjectURL(file)
  const anchor = document.createElement("a")
  anchor.href = url
  anchor.download = file.name
  anchor.click()
  setTimeout(() => URL.revokeObjectURL(url), DOWNLOAD_URL_LIFETIME_MS)
}

export const useReferralShare = () => {
  const [feedback, setFeedback] = useState<ShareFeedback>(null)
  const timer = useRef<ReturnType<typeof setTimeout> | null>(null)
  const sharing = useRef(false)
  const bridge = useNativeBridge()

  useEffect(
    () => () => {
      if (timer.current) clearTimeout(timer.current)
    },
    []
  )

  const flash = useCallback((next: ShareFeedback) => {
    setFeedback(next)
    if (timer.current) clearTimeout(timer.current)
    timer.current = setTimeout(() => setFeedback(null), FEEDBACK_MS)
  }, [])

  const copy = useCallback(
    async (kind: "code" | "link", value: string) => {
      try {
        await navigator.clipboard.writeText(value)
        flash(kind)
      } catch {
        flash(copyWithSelection(value) ? kind : "failed")
      }
    },
    [flash]
  )

  const share = useCallback(
    async (text: string, fallbackLink: string, channel?: ShareChannel) => {
      if (sharing.current) return
      sharing.current = true
      try {
        if (channel && canShareNatively(bridge, "share.channel")) {
          if ((await shareNatively(bridge, "share.channel", { channel, message: text, url: fallbackLink })) !== "unavailable") return
        }
        if (canShareNatively(bridge, "share.open")) {
          if ((await shareNatively(bridge, "share.open", { message: text })) !== "unavailable") return
        } else if (canShareViaLegacyHost() && shareViaLegacyHost(text)) {
          return
        }

        if (typeof navigator.share === "function") {
          try {
            await navigator.share({ text })
            return
          } catch (error) {
            if (isCancelled(error) || isShareInProgress(error)) return
          }
        }
        await copy("link", fallbackLink)
      } catch {
        flash("failed")
      } finally {
        sharing.current = false
      }
    },
    [bridge, copy, flash]
  )

  const shareStory = useCallback(
    async (data: ShareStoryData, image: () => Promise<Blob>) => {
      if (sharing.current) return
      sharing.current = true
      try {
        if (canShareNatively(bridge, "share.story") && (await shareNatively(bridge, "share.story", data)) !== "unavailable") return

        const copied = navigator.clipboard?.writeText(data.shareUrl).then(
          () => true,
          () => false
        )
        const file = new File([await image()], STORY_FILE_NAME, { type: "image/png" })
        if (typeof navigator.share === "function" && navigator.canShare?.({ files: [file] })) {
          try {
            await navigator.share({ files: [file] })
            if (await copied) flash("story")
            return
          } catch (error) {
            if (isCancelled(error) || isShareInProgress(error)) return
          }
        }
        downloadFile(file)
        flash((await copied) ? "story" : "failed")
      } catch {
        flash("failed")
      } finally {
        sharing.current = false
      }
    },
    [bridge, flash]
  )

  const nativeShare = canShareNatively(bridge, "share.channel") || canShareNatively(bridge, "share.open")

  return { feedback, copy, share, shareStory, nativeShare }
}
